import { type Address as AddressType, useGetAddress } from '@/api/address';
import { AutoComplete, type AutoCompleteOption } from '../Common/AutoComplete';
import { useState, useEffect, useRef, useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useCalculateTax } from '@/api/tax';
import { parseAddress } from '@/utils/address';
import { formatCanadianPostalCode } from '@/utils/zipCode';
import { useTranslate } from '@/hooks/useTranslate';
import { MIN_VALID_ADDRESS_LENGTH } from '@/utils/validation';

interface AddressProps {
  onValidate: (isValid: boolean) => void;
  onSelect?: (address: AddressType) => void;
}

export const Address = ({ onValidate, onSelect }: AddressProps) => {
  const {
    actions: { updatePriceBreakdown, updateAddressDetails },
    addressDetails,
    customer,
  } = useContext(CheckoutContext);
  const [addressList, setAddressList] = useState<
    { label: string; value: string; zipCode: string; addressObj: AddressType }[]
  >([]);
  const [inputValue, setInputValue] = useState(addressDetails?.address ?? '');
  const [error, setError] = useState('');
  const { mutateAsync: getAddress, isPending } = useGetAddress();
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { mutateAsync: calculateTax } = useCalculateTax();
  const { t } = useTranslate();
  const countryCode = customer?.taxCountry ?? customer?.geoLocator?.countryCode2;
  const isCanada = countryCode === 'CA';

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (!inputValue.trim()) {
      setAddressList([]);
      return;
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        const { addressResults } = await getAddress(inputValue);
        addressResults?.length > 0
          ? setAddressList(
              addressResults.map((addr) => ({
                label: addr?.address,
                value: addr?.address,
                zipCode: addr?.zipCode,
                addressObj: addr,
              }))
            )
          : setAddressList([]);
      } catch (error) {
        console.error('Error fetching addresses:', error);
        setAddressList([]);
      }
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [inputValue, getAddress]);

  const handleChange = (value: string) => {
    setInputValue(value);
    if (value?.length >= MIN_VALID_ADDRESS_LENGTH) {
      onValidate(true);
      setError('');
    } else {
      onValidate(false);
      setError(t('fieldErrors.invalidAddress'));
    }
  };

  const validateAddress = (address?: string) => {
    try {
      if (address) {
        parseAddress(address);
        onValidate(true);
        setError('');
      } else {
        onValidate(false);
        setError(t('fieldErrors.invalidAddress'));
      }
    } catch (error) {
      console.error('Error parsing address:', error);
      onValidate(false);
      setError(t('fieldErrors.invalidAddress'));
    }
  };

  const handleSelect = async (selectedOption: AutoCompleteOption) => {
    const selectedAddress = addressList.find((addr) => addr.value === selectedOption.value);
    const { priceBreakdown, zipCode, state } = await calculateTax({
      address: selectedAddress?.addressObj?.address ?? '',
      zipCode: selectedAddress?.zipCode ?? '',
    });
    // Format Canadian postal code if needed
    const formattedZipCode = zipCode && isCanada ? formatCanadianPostalCode(zipCode) : (zipCode ?? '');
    updateAddressDetails({ address: selectedAddress?.value ?? '', zipCode: formattedZipCode, state: state ?? '' });
    updatePriceBreakdown(priceBreakdown);
    validateAddress(selectedAddress?.value);
    if (selectedAddress?.addressObj) {
      onSelect?.(selectedAddress?.addressObj);
    }
  };

  useEffect(() => {
    if (addressDetails?.address) {
      validateAddress(addressDetails?.address);
    }
  }, []);

  return (
    <div className="w-full">
      <AutoComplete
        onInputChange={handleChange}
        options={addressList}
        onSelect={handleSelect}
        value={addressDetails?.address ?? ''}
        placeholder={t('address')}
        className={'w-full'}
        error={error}
        disabled={false}
        isLoading={isPending}
        disableFiltering={true}
      />
    </div>
  );
};
